/**
 * Defensive unwrap for Scrips Master endpoint rows.
 *
 * **Why this exists** — multiple `/api/Master/*` endpoints
 * inconsistently ship rows in TWO different shapes:
 *
 *   1. **Flat** — `{ code: "91301", display: "Covid…", system: "..." }`
 *   2. **Wrapped** — `{ code: { code: "91301", display: "Covid…", … } }`
 *      (the actual Coding is nested under a `code` key).
 *
 * The wrapping is a backend inconsistency that has bitten the chart
 * editors repeatedly:
 *
 *   - `DiagnosisCodes`     → wrapped (PR #225 commit `9295a3f`, browser-fix)
 *   - `ProcedureCodesList` → wrapped (PR #232 commit `196d780`, browser-fix)
 *   - `VaccinationCodesList` → wrapped (PR #253 commit `d35046d`, browser-fix)
 *
 * Each time the bug surfaces it's the same crash pattern: the picker
 * stringifies the wrapped row, sees `display` as an object (not a
 * string), and crashes on `.trim()` — plus the row key resolves to
 * `--` (empty), so React fires duplicate-key warnings. The submit
 * handler then nests the wrapped row AGAIN under its own `code`
 * field and the backend rejects with 400.
 *
 * **This helper centralizes the defensive unwrap** so every chart
 * picker can apply it uniformly. The shape returned is the inner
 * record (a `Record<string, unknown>`) — callers normalize from
 * there into their specific Coding type (with `Definition` PascalCase,
 * `description`, `shortName`, etc. — whatever the wire expects).
 *
 * Safe on FLAT rows: when `row.code` is a string (or null/missing),
 * the helper returns the row itself. Adding this helper at a flat
 * picker is a no-op — there's no downside to applying it
 * defensively everywhere.
 *
 * Tracked: **PROD-842** wire-literal cleanup includes consolidating
 * the master-endpoint row shape.
 */

export function unwrapMasterRow(
  row: unknown,
): Record<string, unknown> | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  // Detect wrapped shape: `code` field is itself an object (not a
  // primitive code value). The inner object IS the real Coding.
  if (r.code !== null && typeof r.code === 'object') {
    return r.code as Record<string, unknown>;
  }
  return r;
}

/** Type-narrowing string accessor — returns the string value of a
 *  field, or null when the field is missing, null, or not a string.
 *  Centralises the `typeof === 'string'` guards that prevent
 *  `.trim()` from crashing on object values. */
export function readStringField(
  row: Record<string, unknown> | null | undefined,
  field: string,
): string | null {
  if (!row) return null;
  const v = row[field];
  return typeof v === 'string' ? v : null;
}
